<script lang="ts">
  /* Cart — shopping cart with qty controls and checkout flow.
     Optional `?supplier=<slug>` query param scopes to a cross-tenant cart. */
  import DateInput from "$components/DateInput.svelte";
  import { onMount } from "svelte";
  import { push } from "svelte-spa-router";
  import { _ } from "svelte-i18n";
  import AppShell from "$components/AppShell.svelte";
  import InfoTooltip from "$components/InfoTooltip.svelte";
  import { auth } from "$lib/auth";
  import { formatEur } from "$lib/format";
  import {
    fetchCart,
    updateCartItem,
    removeCartItem,
    clearCart,
    checkout,
    type Cart,
    type CartLineIssue,
  } from "$lib/orders";
  import { fetchSuppliers, type SupplierLink } from "$lib/suppliers";
  import { issueDescriptor } from "$lib/orderIssues";

  let { params }: { params: { slug: string } } = $props();
  const slug = $derived(params?.slug ?? "");
  type CompanyType = "manufacturer" | "wholesaler" | "shop";
  const companyType = $derived<CompanyType>(
    ($auth.memberships.find((m) => m.tenant.slug === slug)?.tenant.company_type as CompanyType) ?? "manufacturer",
  );
  const cartLabel = $derived(companyType === "wholesaler" ? $_("cart.procurementCart") : $_("cart.breadcrumb.cart"));
  const cartTitle = $derived(companyType === "wholesaler" ? $_("cart.procurementCart") : $_("cart.title"));
  const cartDescription = $derived(
    companyType === "wholesaler"
      ? $_("cart.desc.wholesaler")
      : $_("cart.desc.shop"),
  );

  // Supplier from `?supplier=<slug>` if present (cross-tenant cart).
  // svelte-spa-router uses hash routing: location.hash = "#/path?supplier=foo".
  function readSupplier(): string {
    if (typeof window === "undefined") return "";
    const h = window.location.hash;
    const qIdx = h.indexOf("?");
    if (qIdx === -1) return "";
    return new URLSearchParams(h.slice(qIdx + 1)).get("supplier") ?? "";
  }
  let supplier = $state(readSupplier());
  const supplierParam = $derived(supplier || undefined);

  let cart = $state<Cart | null>(null);
  let loading = $state(true);
  let checkingOut = $state(false);
  // One key per checkout attempt: a retried submit reuses it, so a flaky
  // network can never create two orders.
  let checkoutKey = $state("");
  let checkoutError = $state("");
  let showCheckout = $state(false);
  let supplierInfo = $state<SupplierLink | null>(null);

  // Checkout form
  let buyerName = $state("");
  let buyerEmail = $state("");
  let shippingAddress = $state("");
  let notes = $state("");
  let deliveryDate = $state("");
  let deliveryTime = $state("");

  // Server readiness verdicts → human text. The rules live in
  // apps/orders/readiness; the mapping is shared with the composer.
  function issueText(iss: CartLineIssue): string {
    const d = issueDescriptor(iss);
    return d ? $_(d.key, d.values ? { values: d.values } : undefined) : iss.message;
  }

  async function load() {
    loading = true;
    try {
      if (!supplierParam) {
        cart = null;
        supplierInfo = null;
        return;
      }
      cart = await fetchCart(slug, supplierParam);
      try {
        const all = await fetchSuppliers(slug);
        supplierInfo = all.find((l) => l.seller_slug === supplier) ?? null;
      } catch { /* ignore */ }
    } finally {
      loading = false;
    }
  }

  async function updateQty(itemId: number, qty: number) {
    cart = await updateCartItem(slug, itemId, qty, supplierParam);
  }

  async function removeItem(itemId: number) {
    await removeCartItem(slug, itemId, supplierParam);
    cart = await fetchCart(slug, supplierParam);
  }

  async function doClearCart() {
    if (!confirm($_("cart.clearConfirm"))) return;
    await clearCart(slug, supplierParam);
    cart = await fetchCart(slug, supplierParam);
  }

  async function submitCheckout() {
    checkingOut = true;
    checkoutError = "";
    try {
      if (!checkoutKey) checkoutKey = crypto.randomUUID();
      const order = await checkout(slug, {
        buyer_name: buyerName,
        buyer_email: buyerEmail,
        shipping_address: shippingAddress,
        notes,
        desired_delivery_at: deliveryDate ? `${deliveryDate}T${deliveryTime || "00:00"}` : "",
        submit: true,
        idempotency_key: checkoutKey,
      }, supplierParam);
      checkoutKey = "";
      push(`/t/${slug}/orders/${order.code}`);
    } catch (e: any) {
      checkoutError = e?.message ?? $_("cart.checkout.fallbackError");
    } finally {
      checkingOut = false;
    }
  }


  const subtotal = $derived(cart?.subtotal_eur ?? 0);
  // VAT from each line's own rate — not a flat country guess.
  const vat = $derived(
    (cart?.items ?? []).reduce(
      (acc, i) => acc + (Number(i.line_total_eur) * (Number(i.vat_rate) || 0)) / 100,
      0,
    ),
  );
  const total = $derived(subtotal + vat);

  onMount(() => {
    const onHash = () => { supplier = readSupplier(); };
    window.addEventListener("hashchange", onHash);
    if (slug) load();
    return () => window.removeEventListener("hashchange", onHash);
  });

  // Reload if the supplier query param changes while mounted.
  $effect(() => {
    void supplier;
    void companyType;
    if (slug) load();
  });
</script>

<AppShell tenantSlug={slug} active="cart">
  <div class="cart-page">
    <!-- Breadcrumb hidden on the top-level (Procurement) cart — it's a single-level
         page reached directly from the sidebar. Kept for the supplier drill-down. -->
    {#if supplier}
      <div class="breadcrumb">
        <a href={`#/t/${slug}/dashboard`}>{$_("orders.breadcrumb.dashboard")}</a>
        <span class="sep">›</span>
        <a href={`#/t/${slug}/suppliers`}>{$_("cart.breadcrumb.suppliers")}</a>
        <span class="sep">›</span>
        <a href={`#/t/${slug}/suppliers/${supplier}/products`}>{supplierInfo?.seller_name ?? supplier}</a>
        <span class="sep">›</span>
        <span>{cartLabel}</span>
      </div>
    {/if}
    <div class="title-row">
      <h1 class="page-title">
        {#if supplier}
          {companyType === "wholesaler"
            ? $_("cart.procurementCartSupplier", { values: { name: supplierInfo?.seller_name ?? supplier } })
            : $_("cart.titleSupplier", { values: { name: supplierInfo?.seller_name ?? supplier } })}
        {:else}
          {cartTitle}
        {/if}
      </h1>
      <InfoTooltip text={cartDescription} />
    </div>

    {#if supplier}
      <div class="supplier-banner">
        {$_("cart.supplierBanner", { values: { name: supplierInfo?.seller_name ?? supplier } })}
      </div>
    {/if}

    {#if !supplier}
      <div class="empty-state card">
        <p>{$_("cart.chooseSupplierFirst")}</p>
        <a href={`#/t/${slug}/suppliers`} class="btn btn--primary">{$_("cart.chooseSupplier")}</a>
      </div>
    {:else if loading}
      <div class="loading-center">{$_("orders.loading")}</div>
    {:else if !cart || cart.items.length === 0}
      <div class="empty-state card">
        <p>{$_("cart.empty")}</p>
        <a href={`#/t/${slug}/suppliers/${supplier}/products`} class="btn btn--primary">
          {$_("cart.browseSupplierCatalog", { values: { name: supplierInfo?.seller_name ?? $_("cart.browseSupplierFallback") } })}
        </a>
      </div>
    {:else}
      <div class="cart-grid">
        <!-- Items -->
        <div class="card items-card">
          <div class="items-header">
            <span class="items-count">{$_("cart.items", { values: { count: cart.item_count } })}</span>
            <button type="button" class="btn btn--ghost btn--sm" onclick={doClearCart}>{$_("cart.clearAll")}</button>
          </div>

          <div class="items-list">
            {#each cart.items as item}
              <div class="cart-item">
                <div class="item-info">
                  <div class="item-name">{item.product_name}</div>
                  {#if item.product_sku}
                    <div class="item-sku">{item.product_sku}</div>
                  {/if}
                  {#each item.issues ?? [] as iss}
                    <div class="item-issue">
                      <span class="material-icons-outlined" aria-hidden="true">error_outline</span>
                      {issueText(iss)}
                      {#if iss.suggested}
                        <button type="button" class="issue-fix"
                          onclick={() => updateQty(item.id, iss.suggested!)}>
                          {$_("orders.issue.setSuggested", { values: { qty: iss.suggested } })}
                        </button>
                      {/if}
                    </div>
                  {/each}
                </div>
                <div class="item-controls">
                  <div class="qty-stepper">
                    <button type="button" class="qty-btn"
                      onclick={() => updateQty(item.id, item.quantity - 1)}
                      disabled={item.quantity <= 1}>−</button>
                    <span class="qty-val">{item.quantity}</span>
                    <button type="button" class="qty-btn"
                      onclick={() => updateQty(item.id, item.quantity + 1)}>+</button>
                  </div>
                  <div class="item-price">{formatEur(item.line_total_eur)}</div>
                  <button type="button" class="remove-btn"
                    onclick={() => removeItem(item.id)} title={$_("cart.removeItem")}>✕</button>
                </div>
              </div>
            {/each}
          </div>
        </div>

        <!-- Summary + checkout -->
        <div class="side-col">
          <div class="card summary-card">
            <h3 class="card-title">{$_("cart.summary.title")}</h3>
            <div class="summary-row"><span>{$_("cart.summary.subtotal")}</span><span>{formatEur(subtotal)}</span></div>
            <div class="summary-row muted"><span>{$_("cart.summary.vatEstimate")}</span><span>{formatEur(vat)}</span></div>
            <div class="summary-total"><span>{$_("cart.summary.totalEstimate")}</span><span>{formatEur(total)}</span></div>

            {#if cart && !cart.sendable}
              <div class="send-blocked">{$_("orders.issue.fixBeforeSend")}</div>
            {/if}
            <button type="button" class="btn btn--primary btn--block"
              disabled={!cart?.sendable}
              onclick={() => { showCheckout = true; }}>
              {$_("cart.proceedCheckout")}
            </button>
          </div>
        </div>
      </div>
    {/if}
  </div>
</AppShell>

<!-- Checkout modal -->
{#if showCheckout}
  <div class="modal-backdrop" role="presentation" onclick={() => { showCheckout = false; }}>
    <div class="modal" role="dialog" aria-modal="true" aria-label={$_("cart.checkout.title")}
      tabindex="-1"
      onclick={(e) => e.stopPropagation()}
      onkeydown={(e) => e.key === "Escape" && (showCheckout = false)}
    >
      <h3 class="modal-title">{$_("cart.checkout.title")}</h3>

      {#if checkoutError}
        <div class="modal-error">{checkoutError}</div>
      {/if}

      <div class="form-field">
        <label class="form-label" for="co-name">{$_("cart.checkout.buyerName")}</label>
        <input id="co-name" type="text" class="form-input" placeholder={$_("cart.checkout.buyerName.placeholder")}
          bind:value={buyerName} />
      </div>
      <div class="form-field">
        <label class="form-label" for="co-email">{$_("cart.checkout.email")}</label>
        <input id="co-email" type="email" class="form-input" placeholder={$_("cart.checkout.email.placeholder")}
          bind:value={buyerEmail} />
      </div>
      <div class="form-field">
        <label class="form-label" for="co-addr">{$_("cart.checkout.shippingAddress")}</label>
        <textarea id="co-addr" class="form-input form-textarea" rows="3"
          placeholder={$_("cart.checkout.shippingAddress.placeholder")} bind:value={shippingAddress}></textarea>
      </div>
      <div class="form-field">
        <label class="form-label" for="co-notes">{$_("cart.checkout.notes")}</label>
        <input id="co-notes" type="text" class="form-input" placeholder={$_("cart.checkout.notes.placeholder")}
          bind:value={notes} />
      </div>
      <div class="form-field">
        <label class="form-label" for="co-delivery">{$_("orders.compose.deliveryAt")}</label>
        <div class="delivery-row">
          <DateInput id="co-delivery" bind:value={deliveryDate} />
          <input type="time" class="form-input" bind:value={deliveryTime} disabled={!deliveryDate}
            aria-label={$_("orders.compose.deliveryAt")} />
        </div>
      </div>

      <div class="modal-actions">
        <button type="button" class="btn btn--ghost" onclick={() => { showCheckout = false; }}>
          {$_("cart.checkout.back")}
        </button>
        <button type="button" class="btn btn--primary" disabled={checkingOut} onclick={submitCheckout}>
          {checkingOut ? $_("cart.checkout.submitting") : $_("cart.checkout.submit")}
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  .cart-page { display: flex; flex-direction: column; gap: 16px; }
  .breadcrumb { font-size: 13px; color: var(--color-text-muted); display: flex; gap: 6px; }
  .breadcrumb a { color: var(--color-primary); text-decoration: none; }
  .sep { color: var(--color-text-muted); }
  .page-title { margin: 0; font-size: 22px; font-weight: 700; color: var(--color-surface-darker); }
  .loading-center { padding: 60px; text-align: center; color: var(--color-text-muted); }

  .empty-state { padding: 48px; text-align: center; display: flex; flex-direction: column;
    align-items: center; gap: 16px; color: var(--color-text-muted); font-size: 15px; }

  .supplier-banner { background: #e3f2fd; border: 1px solid #90caf9; color: #0d47a1;
    padding: 10px 14px; border-radius: var(--radius); font-size: 13px; }

  .cart-grid { display: grid; grid-template-columns: 1fr 300px; gap: 16px; align-items: start; }
  @media (max-width: 800px) { .cart-grid { grid-template-columns: 1fr; } }
  .side-col { display: flex; flex-direction: column; gap: 12px; }

  /* Items card */
  .items-card { padding: 0; overflow: hidden; }
  .items-header { display: flex; align-items: center; justify-content: space-between;
    padding: 14px 20px; border-bottom: 1px solid var(--color-divider); }
  .items-count { font-size: 13px; color: var(--color-text-muted); }
  .items-list { display: flex; flex-direction: column; }

  .cart-item { display: flex; align-items: center; justify-content: space-between;
    padding: 14px 20px; border-bottom: 1px solid var(--color-divider); gap: 12px; }
  .cart-item:last-child { border-bottom: none; }
  .item-info { flex: 1; min-width: 0; }
  .item-name { font-weight: 600; color: var(--color-surface-darker); font-size: 13px;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .item-sku { font-size: 11px; color: var(--color-text-muted); }
  .item-issue { display: flex; align-items: center; gap: 4px; margin-top: 4px;
    font-size: 12px; color: #c62828; }
  .item-issue .material-icons-outlined { font-size: 15px; }
  .issue-fix { background: none; border: 1px solid #c62828; color: #c62828;
    border-radius: var(--radius); font-size: 11px; padding: 1px 8px; cursor: pointer; }
  .issue-fix:hover { background: #fdecea; }
  .send-blocked { background: #fdecea; color: #c62828; font-size: 12px;
    padding: 8px 12px; border-radius: var(--radius); margin-bottom: 10px; }
  .delivery-row { display: flex; gap: 8px; }
  .item-controls { display: flex; align-items: center; gap: 12px; flex-shrink: 0; }
  .item-price { font-weight: 600; color: var(--color-surface-darker); font-size: 14px;
    min-width: 70px; text-align: right; }

  /* Qty stepper */
  .qty-stepper { display: flex; align-items: center; border: 1px solid var(--color-divider);
    border-radius: var(--radius); overflow: hidden; }
  .qty-btn { width: 28px; height: 28px; border: none; background: #fff; cursor: pointer;
    font-size: 16px; color: var(--color-surface-darker); display: flex; align-items: center;
    justify-content: center; }
  .qty-btn:disabled { opacity: 0.35; cursor: default; }
  .qty-btn:hover:not(:disabled) { background: var(--color-bg); }
  .qty-val { padding: 0 10px; font-size: 13px; font-weight: 600;
    color: var(--color-surface-darker); min-width: 28px; text-align: center; }
  .remove-btn { background: none; border: none; cursor: pointer; color: var(--color-text-muted);
    font-size: 14px; padding: 4px; }
  .remove-btn:hover { color: #c62828; }

  /* Summary */
  .summary-card { padding: 20px; }
  .card-title { margin: 0 0 16px; font-size: 14px; font-weight: 700; color: var(--color-surface-darker); }
  .summary-row { display: flex; justify-content: space-between; font-size: 13px;
    padding: 5px 0; color: var(--color-surface-darker); }
  .summary-row.muted { color: var(--color-text-muted); }
  .summary-total { display: flex; justify-content: space-between; font-size: 16px;
    font-weight: 700; color: var(--color-surface-darker); padding: 12px 0 16px;
    border-top: 1px solid var(--color-divider); margin-top: 8px; }

  /* Buttons */
  .btn { display: inline-flex; align-items: center; justify-content: center; gap: 6px;
    padding: 8px 16px; border-radius: var(--radius); border: none; cursor: pointer;
    font-size: 13px; font-weight: 600; text-decoration: none; }
  .btn:disabled { opacity: 0.5; cursor: default; }
  .btn--primary { background: var(--color-primary); color: #fff; }
  .btn--ghost { background: transparent; color: var(--color-primary);
    border: 1px solid var(--color-primary); }
  .btn--block { width: 100%; }
  .btn--sm { padding: 6px 12px; font-size: 12px; }

  /* Modal */
  .modal-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,0.35);
    display: flex; align-items: center; justify-content: center; z-index: 1000; }
  .modal { background: #fff; border-radius: var(--radius); padding: 28px;
    width: 440px; max-width: 95vw; box-shadow: 0 8px 32px rgba(0,0,0,0.18); }
  .modal-title { margin: 0 0 20px; font-size: 17px; font-weight: 700; color: var(--color-surface-darker); }
  .modal-error { background: #fdecea; color: #c62828; padding: 10px 14px;
    border-radius: var(--radius); font-size: 13px; margin-bottom: 16px; }
  .modal-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 24px; }

  .form-field { display: flex; flex-direction: column; gap: 6px; margin-bottom: 14px; }
  .form-label { font-size: 12px; font-weight: 600; color: var(--color-text-muted); }
  .form-input { padding: 8px 12px; border: 1px solid var(--color-divider);
    border-radius: var(--radius); font-size: 14px; width: 100%; box-sizing: border-box; }
  .form-textarea { resize: vertical; font-family: inherit; }
</style>
